import type { ChangeEvent, FormEvent } from 'react'
import type { SingleMatch } from 'types/match'
import type { Offer } from 'types/seller'

import { useState } from 'react'
import styled from 'styled-components'
import { useStore } from 'store'
import copy from 'copy.json'

import Modal from 'components/Modal'
import Button from 'components/Button'
import Status from 'components/Status'
import Spinner from 'components/Spinner'

const text = copy.buyer.demand_details.modal.delete_reason

type Props = {
  title: string
  status?: SingleMatch['status'] | Offer['status']
  resourceId: number
  isModalOpen: boolean
  onModalClose: () => void
  onModalConfirmation: (reason: string) => Promise<boolean>
  confirmButtonLabel: string
  closeButtonLabel: string
}

export default function DeleteResourceModal({
  title,
  status,
  resourceId,
  isModalOpen,
  onModalClose,
  onModalConfirmation,
  confirmButtonLabel,
  closeButtonLabel
}: Props) {
  const { fieldErrors, clearFieldErrors } = useStore()
  const [deleteReason, setDeleteReason] = useState('')
  const [loading, setLoading] = useState(false)

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    clearFieldErrors()
    setDeleteReason(event.target.value)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!deleteReason) return

    setLoading(true)

    const success = await onModalConfirmation(deleteReason)

    setLoading(false)

    if (success) handleClose()
  }

  const handleClose = () => {
    setDeleteReason('')
    clearFieldErrors()
    onModalClose()
  }

  if (!status) return null

  return (
    <StyledModal isOpen={isModalOpen} onClose={handleClose}>
      <Form onSubmit={handleSubmit}>
        <h3>{title}</h3>

        <DeleteOffer>
          <Details>
            <p>{text.status}</p>
            <Status statusType={status} />
          </Details>

          <Details>
            <p>{text.id}</p>
            <p style={{ font: 'var(--400-13)' }}>{resourceId}</p>
          </Details>

          <Details>
            <p>{text.reason}</p>

            <TextareaWrapper>
              <textarea value={deleteReason} onChange={handleChange} />
              {fieldErrors.reason && <span>{fieldErrors.reason}</span>}
            </TextareaWrapper>
          </Details>
        </DeleteOffer>

        <Buttons>
          <Button warning type="submit" disabled={!deleteReason}>
            {loading ? <Spinner size={16} /> : confirmButtonLabel}
          </Button>
          <Button secondary warning type="button" onClick={handleClose}>
            {closeButtonLabel}
          </Button>
        </Buttons>
      </Form>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  max-width: 868px;
  width: 100%;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;

  width: 100%;

  p {
    max-width: 424px;
  }
`

const DeleteOffer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 100%;
  padding: 16px max(8px, calc((100% - 600px) / 2));

  background: var(--very-light-blue);
`

const Details = styled.div`
  display: flex;
  gap: 20px;

  font: var(--600-13);

  p:first-of-type {
    width: 90px;
    text-align: left;
  }
`

const TextareaWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  textarea {
    align-self: stretch;
    min-height: 128px;
    padding: 4px;
    border: 1px solid var(--light-gray);
    border-radius: 4px;
    font: var(--400-13);
  }

  span {
    color: var(--red);
    font: var(--400-11);
  }
`

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  width: 100%;
  max-width: 424px;
  margin-top: 8px;
  margin-bottom: 24px;

  button {
    width: 100%;
  }
`
